import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, ListItemText, Snackbar, TextField } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllUsernames } from "../../../utils/AccountService";
import { removeSharedUser, shareRecipe } from "../../../utils/RecipeService";

export type ShareRecipeDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    recipeId: string;
    sharedUsers: string[];
    setSharedUsers: Dispatch<SetStateAction<string[]>>;
}

const ShareRecipeDialog = (props: ShareRecipeDialogProps) => {
    const {t} = useTranslation();
    const [existingUsers, setExistingUsers] = useState<string[]>([]);
    const [availableUsers, setAvailableUsers] = useState<string[]>([]);
    const [newSharedUser, setNewSharedUser] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>();

    const updateAvailableUsers = useCallback((newSharedUsers: string[]) => {
        const newAvailableUsers = existingUsers.filter((user: string) => !newSharedUsers.includes(user));
        setAvailableUsers(newAvailableUsers);
    }, [existingUsers]);

    useEffect(() => {
        getAllUsernames().then(usernames => {
            setExistingUsers(usernames);
            updateAvailableUsers(props.sharedUsers);
        });
    }, [props]);

    const addNewSharedUser = useCallback(() => {
        if(newSharedUser && !props.sharedUsers.includes(newSharedUser) && availableUsers.includes(newSharedUser)) {
            shareRecipe(props.recipeId, newSharedUser).then(response => {
                if(response.ok) {
                    props.setSharedUsers((currentSharedUsers: string[]) => {
                        const newSharedUsers = [...currentSharedUsers];
                        newSharedUsers.push(newSharedUser);
                        updateAvailableUsers(newSharedUsers);
                        return newSharedUsers;
                    });
                } else {
                    setSnackbarMessage(t('couldNotShareRecipe'));
                    setSnackbarOpen(true);
                }
            });
        }
        setNewSharedUser(null);
    }, [newSharedUser, props, availableUsers, updateAvailableUsers, t]);

    const removeSharedUserForRecipe = useCallback((username: string) => {
        removeSharedUser(props.recipeId, username).then(response => {
            if(response.ok) {
                props.setSharedUsers((currentSharedUsers: string[]) => {
                    const newSharedUsers = currentSharedUsers.filter((sharedUser: string) => sharedUser !== username);
                    updateAvailableUsers(newSharedUsers);
                    return newSharedUsers; 
                });
            } else {
                setSnackbarMessage(t('couldNotRemoveShared'));
                setSnackbarOpen(true);
            }
        });
    }, [props, t, updateAvailableUsers]);

    return (
        <Dialog open={props.open} onClose={() => props.setOpen(false)}>
            <DialogTitle>{t('shareRecipeTitle')}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('shareRecipeContent')}
                </DialogContentText>
                <div style={{display: 'flex', flexDirection: 'row', marginTop: '10px', justifyContent: 'center'}}>
                    <List style={{maxHeight: '160px', width: '300px', overflowY: 'auto'}}>
                        {props.sharedUsers.map(sharedUser => {
                            return (
                                <ListItem secondaryAction={
                                    <IconButton edge='end' onClick={() => removeSharedUserForRecipe(sharedUser)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }>
                                    <ListItemText primary={sharedUser} />
                                </ListItem>
                            );
                        })
                        }
                    </List>
                </div>
                <div style={{display: 'flex', flexDirection: 'row', marginTop: '10px', justifyContent: 'center'}}>
                    <Autocomplete options={availableUsers} getOptionLabel={option => option} onChange={(e, value) => setNewSharedUser(value!)} value={newSharedUser}
                        renderInput={(params) => <TextField {...params} label={t('username')} variant="standard" onChange={(newText) => setNewSharedUser(newText.target.value)} value={newSharedUser} />} style={{minWidth: '250px'}} />
                    <IconButton aria-label="add" size="large" style={{marginLeft: '10px'}} onClick={addNewSharedUser}>
                        <AddIcon fontSize="inherit" /> 
                    </IconButton>
                </div>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={() => props.setOpen(false)}>{t('close')}</Button>
            </DialogActions>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={(e) => setSnackbarOpen(false)}
            >
                 <Alert onClose={(e) => setSnackbarOpen(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
                    {snackbarMessage}
                 </Alert>
            </Snackbar>
        </Dialog>
    )
}

export default ShareRecipeDialog;
